"use client"

import { useEffect, useRef } from "react"
import "./search-shift.css"

/**
 * "The Search Shift" — 1:1 port of the approved Claude Design export
 * `Search Shift Section.dc.html` (The_shift_section / Velova Hero Visual
 * v2 Restructure package): the animated running-shoes comparison.
 * Placed on the homepage between the AI-platform marquee and the
 * sections that follow it.
 *
 * Markup, copy and visual rules match the export exactly; the prototype
 * scaffolding (x-dc wrapper, support runtime, the "AI platform marquee
 * ends here" placeholder) was removed. Styles live in the scoped
 * `velova-search-shift-*` stylesheet next to this file, and the export's
 * DCLogic looping entrance animation is ported below (same phases and
 * timings, IntersectionObserver play/pause, reduced-motion → static,
 * pause always lands on the finished state). SVG marks come from the
 * export's `assets/` and are served from /public/search-shift/.
 */

const CYCLE_MS = 11800

export function SearchShift() {
  const rootRef = useRef<HTMLElement>(null)

  useEffect(() => {
    const root = rootRef.current
    if (!root) return

    const all = (sel: string) => Array.from(root.querySelectorAll<HTMLElement>(sel))
    const one = (sel: string) => root.querySelector<HTMLElement>(sel)
    const els = {
      q: one('[data-ss="q"]'),
      links: all('[data-ss="link"]'),
      nThen: one('[data-ss="n-then"]'),
      rule: one('[data-ss="rule"]'),
      arrow: one('[data-ss="arrow"]'),
      answer: one('[data-ss="answer"]'),
      prose: one('[data-ss="prose"]'),
      brands: all('[data-ss="brand"]'),
      badge: one('[data-ss="badge"]'),
      nNow: one('[data-ss="n-now"]'),
    }

    let timers: number[] = []
    let loopTimer: number | undefined
    let playing = false

    const set = (el: HTMLElement | null, props: Record<string, string>) => {
      if (el) Object.assign(el.style, props)
    }
    const setAll = (list: HTMLElement[], props: Record<string, string>) =>
      list.forEach((el) => set(el, props))
    const at = (ms: number, fn: () => void) => {
      timers.push(window.setTimeout(fn, ms))
    }
    const clear = () => {
      timers.forEach(clearTimeout)
      timers = []
      if (loopTimer) clearTimeout(loopTimer)
    }

    // finished state, for stills, reduced motion and any pause
    const complete = () => {
      const on = { transition: "none", opacity: "1", transform: "none" }
      setAll(els.links.concat(els.brands), on)
      ;[els.q, els.answer, els.prose, els.badge, els.nThen, els.nNow, els.arrow].forEach((el) =>
        set(el, on),
      )
      set(els.rule, { transition: "none", transform: "scaleY(1)" })
    }

    const cycle = () => {
      // ---- reset, transitions suppressed so nothing animates backwards ----
      setAll(els.links, { transition: "none", opacity: "0", transform: "translateY(9px)" })
      setAll(els.brands, { transition: "none", opacity: "0", transform: "translateY(10px)" })
      set(els.q, { transition: "none", opacity: "0", transform: "translateY(7px)" })
      set(els.rule, { transition: "none", transform: "scaleY(0)" })
      set(els.arrow, { transition: "none", opacity: "0", transform: "translateX(-10px)" })
      set(els.answer, { transition: "none", opacity: "0", transform: "translateY(12px)" })
      set(els.prose, { transition: "none", opacity: "0", transform: "translateY(6px)" })
      set(els.badge, { transition: "none", opacity: "0" })
      set(els.nThen, { transition: "none", opacity: "0" })
      set(els.nNow, { transition: "none", opacity: "0" })

      requestAnimationFrame(() =>
        requestAnimationFrame(() => {
          setAll(els.links, {
            transition: "opacity .5s ease, transform .5s cubic-bezier(.2,.7,.2,1)",
          })
          setAll(els.brands, {
            transition: "opacity .55s ease, transform .55s cubic-bezier(.2,.7,.2,1)",
          })
          set(els.q, { transition: "opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1)" })
          set(els.rule, { transition: "transform .9s cubic-bezier(.4,0,.2,1)" })
          set(els.arrow, {
            transition: "opacity .6s ease, transform .6s cubic-bezier(.2,.7,.2,1)",
          })
          set(els.answer, {
            transition: "opacity .7s ease, transform .7s cubic-bezier(.2,.7,.2,1)",
          })
          set(els.prose, {
            transition: "opacity .6s ease, transform .6s cubic-bezier(.2,.7,.2,1)",
          })
          set(els.badge, { transition: "opacity .5s ease" })
          set(els.nThen, { transition: "opacity .6s ease" })
          set(els.nNow, { transition: "opacity .6s ease" })
        }),
      )

      // ---- PHASE 1 · the buyer asks --------------------------------------
      at(120, () => set(els.q, { opacity: "1", transform: "none" }))

      // ---- PHASE 2 · the old world fills in, then its count --------------
      els.links.forEach((el, i) =>
        at(700 + i * 200, () => set(el, { opacity: "1", transform: "none" })),
      )
      at(1900, () => set(els.nThen, { opacity: "1" }))

      // ---- PHASE 3 · the shift itself: the axis draws, the arrow settles --
      at(2300, () => set(els.rule, { transform: "scaleY(1)" }))
      at(2900, () => set(els.arrow, { opacity: "1", transform: "none" }))

      // ---- PHASE 4 · the new world: answer, shortlist, count -------------
      at(3600, () => set(els.answer, { opacity: "1", transform: "none" }))
      at(3950, () => set(els.prose, { opacity: "1", transform: "none" }))
      els.brands.forEach((el, i) =>
        at(4600 + i * 220, () => set(el, { opacity: "1", transform: "none" })),
      )
      at(5500, () => set(els.badge, { opacity: "1" }))
      at(5850, () => set(els.nNow, { opacity: "1" }))

      // ---- PHASE 5 · hold the finished state, then restart ----------------
      at(11000, () => {
        const fade = { transition: "opacity .7s ease", opacity: "0" }
        setAll(els.links.concat(els.brands), fade)
        ;[els.q, els.answer, els.prose, els.badge, els.nThen, els.nNow, els.arrow].forEach((el) =>
          set(el, fade),
        )
        set(els.rule, { transition: "transform .7s ease", transform: "scaleY(0)" })
      })
    }

    const loop = () => {
      if (!playing) return
      clear()
      cycle()
      loopTimer = window.setTimeout(loop, CYCLE_MS)
    }
    const play = () => {
      if (!playing) {
        playing = true
        loop()
      }
    }
    // pausing must leave the readable finished state, never a half-built one
    const pause = () => {
      playing = false
      clear()
      complete()
    }

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return

    play()
    let io: IntersectionObserver | undefined
    if (window.IntersectionObserver) {
      io = new IntersectionObserver(
        (entries) => (entries[0].isIntersecting ? play() : pause()),
        { threshold: 0.15 },
      )
      io.observe(root.querySelector('[role="group"]') ?? root)
    }

    return () => {
      pause()
      io?.disconnect()
    }
  }, [])

  return (
    <section id="the-shift" ref={rootRef} aria-labelledby="ss-headline" className="velova-search-shift section-default scroll-mt-24">
      <div aria-hidden="true" className="velova-search-shift-glow" />

      <div className="velova-container">

      <header className="velova-search-shift-header">
        <div className="velova-search-shift-eyebrow">
          <span aria-hidden="true" className="velova-search-shift-eyebrow-gem" />
          <span className="velova-search-shift-eyebrow-label">The search shift</span>
        </div>
        <h2 id="ss-headline" className="velova-search-shift-headline">
          Search is becoming an answer.
        </h2>
        <p className="velova-search-shift-editorial">And the answer shapes the shortlist.</p>
        <p className="velova-search-shift-lede">
          Traditional search sent buyers to compare pages. AI search increasingly compares the
          market first—then presents a smaller set of brands, claims and sources inside the answer.
        </p>
      </header>

      {/* transformation canvas: many pages on one side, one synthesized answer on the other */}
      <div
        role="group"
        aria-label="Traditional search returned ten pages to compare; AI search answers with a synthesized shortlist of three running shoes"
        className="velova-search-shift-canvas"
      >
        {/* the one question, asked in both worlds */}
        <div className="velova-search-shift-question">
          <span className="velova-search-shift-question-label">The same buyer question</span>
          <p data-ss="q" className="velova-search-shift-question-text">
            {"“What are the best running shoes for everyday runs?”"}
          </p>
        </div>

        <div className="velova-search-shift-grid">
          {/* THEN */}
          <div className="velova-search-shift-then">
            <span className="velova-search-shift-chip">Then</span>
            <h3 className="velova-search-shift-col-title">Buyers searched.</h3>
            <p className="velova-search-shift-col-sub">You ranked. They clicked. They compared.</p>

            <div data-ss="link" className="velova-search-shift-searchbar">
              <span aria-hidden="true" className="velova-search-shift-magnifier" />
              <span className="velova-search-shift-searchbar-text">
                best running shoes for everyday runs
              </span>
            </div>

            <ol className="velova-search-shift-serp">
              <li data-ss="link" className="velova-search-shift-result">
                <span className="velova-search-shift-result-domain">nike.com</span>
                <span className="velova-search-shift-result-title">
                  Nike — Running shoes and gear
                </span>
              </li>
              <li data-ss="link" className="velova-search-shift-result">
                <span className="velova-search-shift-result-domain">adidas.com</span>
                <span className="velova-search-shift-result-title">
                  Adidas — Running shoes for every distance
                </span>
              </li>
              <li data-ss="link" className="velova-search-shift-result">
                <span className="velova-search-shift-result-domain">brooksrunning.com</span>
                <span className="velova-search-shift-result-title">
                  Brooks Running — Cushioned running shoes
                </span>
              </li>
              <li data-ss="link" className="velova-search-shift-result">
                <span className="velova-search-shift-result-domain">asics.com</span>
                <span className="velova-search-shift-result-title">
                  ASICS — Everyday running shoes
                </span>
              </li>
              <li
                data-ss="link"
                className="velova-search-shift-result velova-search-shift-more"
              >
                <span aria-hidden="true" className="velova-search-shift-more-dash" />
                <span className="velova-search-shift-more-text">+ 6 more results</span>
              </li>
            </ol>

            <div data-ss="link" className="velova-search-shift-pages">
              <span
                aria-hidden="true"
                className="velova-search-shift-page velova-search-shift-page-current"
              >
                1
              </span>
              <span aria-hidden="true" className="velova-search-shift-page">
                2
              </span>
              <span aria-hidden="true" className="velova-search-shift-page">
                3
              </span>
              <span aria-hidden="true" className="velova-search-shift-page">
                4
              </span>
              <span aria-hidden="true" className="velova-search-shift-pages-next">
                ›
              </span>
            </div>

            <div className="velova-search-shift-stat">
              <span data-ss="n-then" className="velova-search-shift-stat-num">
                10
              </span>
              <span className="velova-search-shift-stat-label">pages to compare</span>
            </div>
            <div className="velova-search-shift-flow">
              <span className="velova-search-shift-flow-step">Rank</span>
              <span aria-hidden="true" className="velova-search-shift-flow-arrow">
                →
              </span>
              <span className="velova-search-shift-flow-step">Click</span>
              <span aria-hidden="true" className="velova-search-shift-flow-arrow">
                →
              </span>
              <span className="velova-search-shift-flow-step">Compare</span>
            </div>
          </div>

          {/* THE SHIFT · a transition axis, not a link between the two states */}
          <div className="velova-search-shift-axis">
            <span data-ss="rule" aria-hidden="true" className="velova-search-shift-axis-rule" />
            <span data-ss="arrow" aria-hidden="true" className="velova-search-shift-axis-arrow">
              <span className="velova-search-shift-axis-halo" />
              <span className="velova-search-shift-axis-outer" />
              <span className="velova-search-shift-axis-inner" />
              <span className="velova-search-shift-axis-glyph">→</span>
            </span>
          </div>

          {/* NOW */}
          <div className="velova-search-shift-now">
            <div className="velova-search-shift-now-head">
              <span className="velova-search-shift-chip velova-search-shift-chip-accent">Now</span>
              <span aria-hidden="true" className="velova-search-shift-now-dot" />
            </div>
            <h3 className="velova-search-shift-col-title">Buyers ask.</h3>
            <p className="velova-search-shift-col-sub">
              AI compares, summarizes and recommends before they reach your website.
            </p>

            <div data-ss="answer" className="velova-search-shift-answer">
              <div className="velova-search-shift-tabs">
                <span className="velova-search-shift-tab velova-search-shift-tab-active">
                  <img src="/search-shift/engine-chatgpt.svg" alt="" aria-hidden="true" />
                  <span className="velova-search-shift-tab-label">ChatGPT</span>
                </span>
                <span className="velova-search-shift-tab">
                  <img src="/search-shift/engine-gemini.svg" alt="" aria-hidden="true" />
                </span>
                <span className="velova-search-shift-tab">
                  <img src="/search-shift/engine-perplexity.svg" alt="" aria-hidden="true" />
                </span>
                <span className="velova-search-shift-tab">
                  <img src="/search-shift/engine-claude.svg" alt="" aria-hidden="true" />
                </span>
              </div>

              <div className="velova-search-shift-convo">
                <div className="velova-search-shift-user-row">
                  <span className="velova-search-shift-user-bubble">
                    best running shoes for everyday runs
                  </span>
                </div>

                <div className="velova-search-shift-searched">
                  <span aria-hidden="true" className="velova-search-shift-searched-marks">
                    <span className="velova-search-shift-mark">
                      <img src="/search-shift/mark-brooks.svg" alt="" />
                    </span>
                    <span className="velova-search-shift-mark">
                      <img src="/search-shift/mark-nike.svg" alt="" />
                    </span>
                    <span className="velova-search-shift-mark">
                      <img src="/search-shift/mark-asics.svg" alt="" />
                    </span>
                  </span>
                  <span className="velova-search-shift-searched-text">Searched 8 sites</span>
                </div>

                <div className="velova-search-shift-reply">
                  <span aria-hidden="true" className="velova-search-shift-reply-avatar">
                    <img src="/search-shift/engine-chatgpt.svg" alt="" />
                  </span>
                  <div data-ss="prose" className="velova-search-shift-prose">
                    <p className="velova-search-shift-prose-p">
                      For everyday runs, three brands come up again and again — each for a
                      different reason:
                    </p>
                    <ol className="velova-search-shift-brands">
                      <li
                        data-ss="brand"
                        className="velova-search-shift-brand velova-search-shift-brand-first"
                      >
                        <span aria-hidden="true" className="velova-search-shift-brand-index">
                          1.
                        </span>
                        <span className="velova-search-shift-brand-text">
                          <strong>Brooks</strong> — softest cushioning
                          <span className="velova-search-shift-cite">1</span>
                        </span>
                        <span data-ss="badge" className="velova-search-shift-badge">
                          <span aria-hidden="true" className="velova-search-shift-badge-dot" />
                          Best fit
                        </span>
                      </li>
                      <li data-ss="brand" className="velova-search-shift-brand">
                        <span aria-hidden="true" className="velova-search-shift-brand-index">
                          2.
                        </span>
                        <span className="velova-search-shift-brand-text">
                          <strong>Nike</strong> — lighter for faster runs
                          <span className="velova-search-shift-cite">2</span>
                        </span>
                      </li>
                      <li data-ss="brand" className="velova-search-shift-brand">
                        <span aria-hidden="true" className="velova-search-shift-brand-index">
                          3.
                        </span>
                        <span className="velova-search-shift-brand-text">
                          <strong>ASICS</strong> — most supportive for distance
                          <span className="velova-search-shift-cite">3</span>
                        </span>
                      </li>
                    </ol>
                    <p className="velova-search-shift-prose-p velova-search-shift-prose-p-last">
                      If most of your running is easy miles, start with Brooks.
                    </p>
                  </div>
                </div>

                <div className="velova-search-shift-actions">
                  <span className="velova-search-shift-action">
                    <span aria-hidden="true" className="velova-search-shift-action-glyph">
                      ⧉
                    </span>
                    Copy
                  </span>
                  <span className="velova-search-shift-action">
                    <span aria-hidden="true" className="velova-search-shift-action-glyph">
                      ↻
                    </span>
                    Regenerate
                  </span>
                  <span className="velova-search-shift-action">
                    <span aria-hidden="true" className="velova-search-shift-action-glyph">
                      △
                    </span>
                    Helpful
                  </span>
                </div>
              </div>

              <div className="velova-search-shift-sources">
                <span className="velova-search-shift-sources-label">Sources</span>
                <div className="velova-search-shift-sources-grid">
                  <span className="velova-search-shift-source">
                    <span className="velova-search-shift-source-head">
                      <span aria-hidden="true" className="velova-search-shift-source-mark">
                        <img src="/search-shift/mark-brooks.svg" alt="" />
                      </span>
                      <span className="velova-search-shift-source-domain">brooksrunning.com</span>
                    </span>
                    <span className="velova-search-shift-source-line">
                      <span aria-hidden="true" className="velova-search-shift-source-num">
                        1
                      </span>
                      <span className="velova-search-shift-source-desc">
                        Cushioned daily trainers
                      </span>
                    </span>
                  </span>
                  <span className="velova-search-shift-source">
                    <span className="velova-search-shift-source-head">
                      <span aria-hidden="true" className="velova-search-shift-source-mark">
                        <img src="/search-shift/mark-nike.svg" alt="" />
                      </span>
                      <span className="velova-search-shift-source-domain">nike.com</span>
                    </span>
                    <span className="velova-search-shift-source-line">
                      <span aria-hidden="true" className="velova-search-shift-source-num">
                        2
                      </span>
                      <span className="velova-search-shift-source-desc">
                        Everyday running shoes
                      </span>
                    </span>
                  </span>
                  <span className="velova-search-shift-source">
                    <span className="velova-search-shift-source-head">
                      <span aria-hidden="true" className="velova-search-shift-source-mark">
                        <img src="/search-shift/mark-asics.svg" alt="" />
                      </span>
                      <span className="velova-search-shift-source-domain">asics.com</span>
                    </span>
                    <span className="velova-search-shift-source-line">
                      <span aria-hidden="true" className="velova-search-shift-source-num">
                        3
                      </span>
                      <span className="velova-search-shift-source-desc">
                        Support and stability
                      </span>
                    </span>
                  </span>
                </div>
              </div>
            </div>

            <div className="velova-search-shift-stat velova-search-shift-stat-now">
              <span
                data-ss="n-now"
                className="velova-search-shift-stat-num velova-search-shift-stat-num-accent"
              >
                3
              </span>
              <span className="velova-search-shift-stat-label velova-search-shift-stat-label-accent">
                brands in the answer
              </span>
            </div>
            <div className="velova-search-shift-flow velova-search-shift-flow-now">
              <span className="velova-search-shift-flow-step">Ask</span>
              <span aria-hidden="true" className="velova-search-shift-flow-arrow">
                →
              </span>
              <span className="velova-search-shift-flow-step">Summarize</span>
              <span aria-hidden="true" className="velova-search-shift-flow-arrow">
                →
              </span>
              <span className="velova-search-shift-flow-step">Shortlist</span>
            </div>
          </div>
        </div>
      </div>

      <div className="velova-search-shift-closing">
        <span aria-hidden="true" className="velova-search-shift-closing-rule" />
        <p className="velova-search-shift-closing-a">Your buyers have not stopped searching.</p>
        <p className="velova-search-shift-closing-b">The decision has moved inside the answer.</p>
      </div>
      </div>
    </section>
  )
}
